/**
 * v1.4.28-h — Shared React hook that polls the two admin health
 * endpoints (`/api/admin/health/smoke` and `/api/admin/health/storage`)
 * on a 60 s cadence and exposes the results.
 *
 * Two consumers today:
 *   * `AppStoreReadiness.jsx`  — big chips at the top of the page.
 *   * `AdminLayout.jsx`        — tiny colored dots in the sidebar so
 *                                failures grab attention from any page.
 *
 * Kept in its own module so the polling logic is DRY. Each mount
 * creates an independent poll loop — sidebar always mounted → always
 * polling; readiness page only polls while open. Response payloads
 * are tiny (< 500 bytes each) so the extra traffic is negligible.
 */
import { useEffect, useState } from "react";
import { getSmokeHealth, getStorageHealth } from "./api";

const DEFAULT_POLL_MS = 60_000;

export function useHealthStatus({ pollMs = DEFAULT_POLL_MS } = {}) {
  const [smoke, setSmoke] = useState(null);
  const [smokeError, setSmokeError] = useState(false);
  const [storage, setStorage] = useState(null);
  const [storageError, setStorageError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const tickSmoke = async () => {
      try {
        const data = await getSmokeHealth();
        if (!cancelled) {
          setSmoke(data);
          setSmokeError(false);
        }
      } catch {
        if (!cancelled) setSmokeError(true);
      }
    };

    const tickStorage = async () => {
      try {
        const data = await getStorageHealth();
        if (!cancelled) {
          setStorage(data);
          setStorageError(false);
        }
      } catch {
        if (!cancelled) setStorageError(true);
      }
    };

    tickSmoke();
    tickStorage();
    const s1 = setInterval(tickSmoke, pollMs);
    const s2 = setInterval(tickStorage, pollMs);
    return () => {
      cancelled = true;
      clearInterval(s1);
      clearInterval(s2);
    };
  }, [pollMs]);

  return { smoke, smokeError, storage, storageError };
}
